import { motion } from 'framer-motion';
import { Cell, Pie, PieChart, ResponsiveContainer } from 'recharts';

const GAUGE_CONFIGS = [
  {
    key: 'occupancyRate',
    label: 'City Occupancy',
    description: 'Cities with citizens',
    color: '#84C341',
    glowColor: 'rgba(132, 195, 65, 0.4)',
  },
  {
    key: 'avgSaturation',
    label: 'Avg Saturation',
    description: 'Population capacity used',
    color: '#00C49F',
    glowColor: 'rgba(0, 196, 159, 0.4)',
  },
  {
    key: 'collabParticipation',
    label: 'Collab Citizens',
    description: 'From partner collections',
    color: '#FFBB28',
    glowColor: 'rgba(255, 187, 40, 0.4)',
  },
  {
    key: 'upgradeAdoption',
    label: 'Upgrades',
    description: 'Cities with upgrades',
    color: '#FF8042',
    glowColor: 'rgba(255, 128, 66, 0.4)',
  },
];

function GaugeRing({ value, color, glowColor, delay = 0 }) {
  const data = [
    { name: 'filled', value: value },
    { name: 'empty', value: 100 - value },
  ];

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.8, rotate: -90 }}
      animate={{ opacity: 1, scale: 1, rotate: 0 }}
      transition={{
        duration: 0.8,
        delay,
        ease: [0.16, 1, 0.3, 1],
      }}
      className="relative"
      style={{
        filter: `drop-shadow(0 0 20px ${glowColor})`,
      }}
    >
      <ResponsiveContainer width="100%" height={180}>
        <PieChart>
          <defs>
            <linearGradient id={`gradient-${color.replace('#', '')}`} x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor={color} stopOpacity={1} />
              <stop offset="100%" stopColor={color} stopOpacity={0.6} />
            </linearGradient>
          </defs>
          <Pie
            data={data}
            cx="50%"
            cy="50%"
            startAngle={220}
            endAngle={-40}
            innerRadius={60}
            outerRadius={75}
            paddingAngle={0}
            dataKey="value"
            stroke="none"
            isAnimationActive={true}
            animationBegin={delay * 1000}
            animationDuration={1200}
            animationEasing="ease-out"
          >
            <Cell fill={`url(#gradient-${color.replace('#', '')})`} />
            <Cell fill="rgba(255,255,255,0.05)" />
          </Pie>
          <Pie
            data={[{ value: 100 }]}
            cx="50%"
            cy="50%"
            startAngle={220}
            endAngle={-40}
            innerRadius={58}
            outerRadius={77}
            dataKey="value"
            stroke="none"
            fill="transparent"
            style={{
              filter: `drop-shadow(0 0 8px ${glowColor})`,
            }}
          />
        </PieChart>
      </ResponsiveContainer>

      <motion.div
        className="absolute inset-0 flex items-center justify-center"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: delay + 0.4, duration: 0.5 }}
      >
        <div className="text-center -mt-2">
          <motion.span
            className="text-3xl lg:text-4xl font-black tracking-tight"
            style={{ color }}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: delay + 0.5, duration: 0.4 }}
          >
            {value}
          </motion.span>
          <span className="text-base lg:text-lg font-medium text-neutral-500">%</span>
        </div>
      </motion.div>

      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: `radial-gradient(circle at 50% 50%, ${glowColor} 0%, transparent 50%)`,
          opacity: 0.3,
        }}
      />
    </motion.div>
  );
}

function GaugeCard({ config, value, index }) {
  const delay = index * 0.15;

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{
        duration: 0.6,
        delay,
        ease: [0.16, 1, 0.3, 1],
      }}
      className="relative group"
    >
      <div
        className="relative rounded-2xl p-4 lg:p-6 overflow-hidden"
        style={{
          background: 'linear-gradient(135deg, rgba(38,38,38,0.8) 0%, rgba(23,23,23,0.9) 100%)',
          border: '1px solid rgba(255,255,255,0.06)',
        }}
      >
        <div
          className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
          style={{
            background: `radial-gradient(circle at 50% 0%, ${config.glowColor} 0%, transparent 60%)`,
          }}
        />

        <div
          className="absolute top-0 left-1/2 -translate-x-1/2 w-24 h-[1px]"
          style={{
            background: `linear-gradient(90deg, transparent 0%, ${config.color} 50%, transparent 100%)`,
          }}
        />

        <div className="relative">
          <GaugeRing value={value} color={config.color} glowColor={config.glowColor} delay={delay} />

          <motion.div
            className="text-center mt-2"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: delay + 0.3, duration: 0.5 }}
          >
            <h3
              className="text-sm lg:text-lg font-bold tracking-wide uppercase"
              style={{
                color: config.color,
                textShadow: `0 0 20px ${config.glowColor}`,
              }}
            >
              {config.label}
            </h3>
            <p className="text-xs lg:text-sm text-neutral-500 mt-1 hidden sm:block">{config.description}</p>
          </motion.div>
        </div>

        <div className="absolute bottom-3 left-3 w-2 h-2 rounded-full bg-neutral-700" />
        <div className="absolute bottom-3 right-3 w-2 h-2 rounded-full bg-neutral-700" />
      </div>
    </motion.div>
  );
}

export default function CensusGauges({ cityStats }) {
  if (!cityStats) return null;

  return (
    <section className="py-12">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="text-center mb-10"
      >
        <h2 className="text-2xl font-black text-neutral-200 tracking-tight uppercase">
          Ecosystem Health
        </h2>
        <div className="mt-2 mx-auto w-16 h-1 rounded-full bg-gradient-to-r from-[#84C341] via-[#00C49F] to-[#FFBB28]" />
      </motion.div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
        {GAUGE_CONFIGS.map((config, index) => (
          <GaugeCard key={config.key} config={config} value={cityStats[config.key] || 0} index={index} />
        ))}
      </div>
    </section>
  );
}
